import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { catalogFerrous, catalogNonFerrous } from '../data/mockData';
import { useSEO } from '../hooks/useSEO';

const CatalogCard = ({ title, description, image }: { title: string, description: string, image?: string }) => {
    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-card overflow-hidden shadow-subtle border border-gray-100 group cursor-pointer"
        >
            <div className="h-48 bg-gray-200 overflow-hidden relative">
                {image ? (
                    <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-100">
                        <span className="text-4xl font-heading opacity-20">LZ</span>
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>

            <div className="p-8">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl md:text-2xl font-heading font-bold text-accent group-hover:text-primary transition-colors">
                        {title}
                    </h3>
                    <ChevronRight className="w-6 h-6 text-gray-300 group-hover:text-secondary group-hover:translate-x-1 transition-all" />
                </div>
                {description && (
                    <p className="text-gray-600 font-body text-base mb-6 h-24 overflow-hidden">
                        {description}
                    </p>
                )}
            </div>
        </motion.div>
    );
};

const Catalog = () => {
    const { t } = useTranslation();

    useSEO({
        title: 'Catálogo de Metales Industriales | Ferrosos y No Ferrosos en América',
        description: 'Explora nuestro inventario y capacidad operativa en América. Suministramos chatarra clasificada, cobre, aluminio y metales bajo estándar internacional.',
    });

    const [activeTab, setActiveTab] = useState<'ferrous' | 'non-ferrous'>('ferrous');

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    return (
        <div className="w-full">
            {/* SECTION 1: PAGE HERO */}
            <section className="min-h-[80dvh] lg:min-h-[60vh] py-16 lg:py-32 massive-container flex flex-col items-center justify-center relative overflow-hidden mt-6 group">
                <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[15s] group-hover:scale-105"
                    style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1518773920067-27b4097401d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80)' }}
                />
                <div className="absolute inset-0 bg-bg-tertiary/90 backdrop-blur-[2px]" />

                <div className="container mx-auto px-4 md:px-12 max-w-5xl text-left md:text-center relative z-10 text-white">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="relative z-10 px-4 md:px-12 max-w-4xl text-center md:text-left mx-auto md:mx-0"
                    >
                        <div className="section-tag-orange mb-6 text-primary-light border-primary/30 md:mx-auto lg:mx-0 inline-flex">
                            {t('catalogPage.tag')}
                        </div>
                        <h1 className="text-3xl md:text-5xl lg:text-7xl font-heading font-medium mb-8 leading-tight text-white drop-shadow-md">
                            {t('catalogPage.title')}
                        </h1>
                        <p className="text-gray-300 font-body text-lg md:text-2xl font-light leading-relaxed max-w-3xl md:mx-auto lg:mx-0">
                            {t('catalogPage.desc')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* SECTION 2: TAB NAVIGATION */}
            <section className="py-12 relative z-20">
                <div className="container mx-auto px-6">
                    <div className="flex justify-center mb-16">
                        <div className="inline-flex bg-gray-100 p-1 rounded-full shadow-inner">
                            <button
                                onClick={() => setActiveTab('ferrous')}
                                className={`px-8 md:px-12 py-4 rounded-full font-heading font-bold text-sm md:text-base tracking-widest transition-all duration-300 ${activeTab === 'ferrous'
                                    ? 'bg-accent text-white shadow-md'
                                    : 'text-gray-500 hover:text-accent hover:bg-gray-200'
                                    }`}
                            >
                                {t('catalogPage.tabFerrous')}
                            </button>
                            <button
                                onClick={() => setActiveTab('non-ferrous')}
                                className={`px-8 md:px-12 py-4 rounded-full font-heading font-bold text-sm md:text-base tracking-widest transition-all duration-300 ${activeTab === 'non-ferrous'
                                    ? 'bg-accent text-white shadow-md'
                                    : 'text-gray-500 hover:text-accent hover:bg-gray-200'
                                    }`}
                            >
                                {t('catalogPage.tabNonFerrous')}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 3: CATALOG GRID */}
            <section className="pb-32 relative z-0">
                <div className="container mx-auto px-6">
                    <AnimatePresence mode="wait">
                        {activeTab === 'ferrous' ? (
                            <motion.div
                                key="ferrous"
                                variants={containerVariants}
                                initial="hidden"
                                animate="show"
                                exit="hidden"
                                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                            >
                                {catalogFerrous.map((item, index) => (
                                    <CatalogCard key={index} title={t(item.title)} description={t(item.description)} image={item.image} />
                                ))}
                            </motion.div>
                        ) : (
                            <motion.div
                                key="non-ferrous"
                                variants={containerVariants}
                                initial="hidden"
                                animate="show"
                                exit="hidden"
                                className="space-y-16"
                            >
                                {/* Non Ferrous Categories */}
                                {Object.entries(catalogNonFerrous).map(([category, items], idx) => (
                                    <div key={idx} className="mb-20 last:mb-0">
                                        <div className="flex items-center mb-8 pb-4 border-b-2 border-gray-100">
                                            <div className="w-2 h-8 bg-primary mr-4 rounded-full"></div>
                                            <h3 className="text-3xl font-heading font-medium text-accent">
                                                {t(category)}
                                            </h3>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                            {items.map((item, id) => (
                                                <div
                                                    key={id}
                                                    className="group relative bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                                                >
                                                    <div className="aspect-square w-full overflow-hidden bg-gray-50">
                                                        <img
                                                            src={item.img}
                                                            alt={t(item.label)}
                                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                        />
                                                    </div>
                                                    <div className="p-5 border-t border-gray-50 flex justify-between items-center group-hover:bg-bg-secondary transition-colors">
                                                        <h4 className="font-heading font-bold text-gray-800 text-sm">{t(item.label)}</h4>
                                                        <ChevronRight className="w-4 h-4 text-primary opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>

            {/* SECTION 4: INVERTED DARK CTA */}
            <section className="pb-24">
                <div className="w-[95%] mx-auto bg-bg-tertiary rounded-block p-12 md:p-24 text-center text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                    <div className="relative z-10 max-w-3xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="max-w-4xl mx-auto"
                        >
                            <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6 text-white text-shadow-sm">
                                {t('catalogPage.finalTitle')}
                            </h2>
                            <p className="text-xl text-gray-200 font-body mb-10 max-w-2xl mx-auto font-light">
                                {t('catalogPage.finalDesc')}
                            </p>
                            <Link to="/contacto" className="btn-secondary inline-flex items-center group shadow-lg hover:shadow-xl transition-shadow text-lg px-8 py-4">
                                {t('catalogPage.finalCta')}
                                <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Catalog;
